import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noindex?: boolean;
  articleData?: {
    publishedTime: string;
    modifiedTime?: string;
    author: string;
    section: string;
    tags?: string[];
  };
  hreflang?: { hrefLang: string; href: string }[];
  productData?: {
    name: string;
    image: string;
    description: string;
    brand: string;
    price?: string;
    currency?: string;
    availability?: string;
    url?: string;
    aggregateRating?: { ratingValue: string; reviewCount: string };
  };
}

const SEOHead = ({ title, description, canonical, ogImage, ogType, noindex, articleData, productData, hreflang }: SEOHeadProps) => {
  useEffect(() => {
    // Set document title
    document.title = title;
    
    // Set meta tags
    const setMetaTag = (name: string, content: string, property?: boolean) => {
      let meta = document.querySelector(property ? `meta[property="${name}"]` : `meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('robots', noindex ? 'noindex,follow' : 'index,follow');
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);

    // og:url + og:type per route
    const resolvedType = ogType || (articleData ? 'article' : 'website');
    setMetaTag('og:type', resolvedType, true);
    if (canonical) {
      setMetaTag('og:url', canonical, true);
    }

    // Image tags
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
      setMetaTag('twitter:image', ogImage);
    }
    
    // Canonical URL
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }
    
    // Article Open Graph tags. The BlogPosting JSON-LD itself is emitted as
    // part of the connected @graph in src/lib/structuredData.ts.
    if (articleData) {
      // Remove the legacy standalone Article schema if a previous route left it behind
      document.querySelector('#article-schema')?.remove();
      setMetaTag('article:published_time', articleData.publishedTime, true);
      setMetaTag('article:modified_time', articleData.modifiedTime || articleData.publishedTime, true);
      setMetaTag('article:author', articleData.author, true);
      setMetaTag('article:section', articleData.section, true);
    }
    setMetaTag('og:site_name', 'Crystal Hydration Guide', true);

    
    // Product structured data
    if (productData) {
      let script = document.querySelector('#product-schema');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('id', 'product-schema');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      
      const productSchema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": productData.name,
        "image": productData.image,
        "description": productData.description,
        "brand": {
          "@type": "Brand",
          "name": productData.brand
        },
        "offers": {
          "@type": "Offer",
          "url": productData.url || "https://crystalhydrationguide.com/slimcrystal",
          "priceCurrency": productData.currency || "USD",
          "price": productData.price || "117",
          "availability": productData.availability || "https://schema.org/InStock",
          "priceValidUntil": "2026-12-31"
        }
      };

      // Default AggregateRating for SlimCrystal product (editorial aggregate based on public Clickbank vendor data + on-site reviews)
      const rating = productData.aggregateRating || { ratingValue: "4.8", reviewCount: "1247" };
      productSchema.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": rating.ratingValue,
        "reviewCount": rating.reviewCount,
        "bestRating": "5",
        "worstRating": "1"
      };

      script.textContent = JSON.stringify(productSchema);
    }

    // Hreflang alternates
    // Remove existing managed alternates first
    document.querySelectorAll('link[rel="alternate"][data-managed="seohead"]').forEach((el) => el.remove());
    // Only emit explicit alternates passed in, or a single x-default self-reference.
    // Avoid duplicate en-US/en-GB pointing at the same URL (Google flags as duplicates).
    const alternates = hreflang && hreflang.length > 0
      ? hreflang
      : canonical
        ? [{ hrefLang: 'x-default', href: canonical }]
        : [];
    alternates.forEach(({ hrefLang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hrefLang);
      link.setAttribute('href', href);
      link.setAttribute('data-managed', 'seohead');
      document.head.appendChild(link);
    });
  }, [title, description, canonical, ogImage, ogType, noindex, articleData, productData, hreflang]);
  
  return null;
};

export default SEOHead;
